import React, { useEffect, useState, Fragment } from 'react';
import { History, Search, Filter, RefreshCw, Calendar, User, BookOpen, ArrowLeft, ArrowRight, CheckCircle, XCircle, AlertTriangle, Info, Download, ChevronDown, ChevronUp, RotateCcw, Eye, Clock, FileText, MoreHorizontal, Users, ListFilter, X } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, DB_SCHEMA } from '../../supabase/config';
import { staffService } from '../../services/staffService';
import { debug } from '../../utils/debug';

interface AssignmentChange {
  id: string;
  timestamp: Date;
  user_email: string;
  action: 'assign' | 'unassign';
  class_id: string;
  class_name: string;
  line_id: string;
  line_name: string;
  teacher_code: string | null;
  teacher_name: string | null;
}

interface Teacher {
  id: string;
  name: string;
  staff_id: string;
  department: string;
}

const AssignmentHistory: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  
  // State
  const [assignmentChanges, setAssignmentChanges] = useState<AssignmentChange[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedChange, setSelectedChange] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeFilters, setActiveFilters] = useState<{
    dateRange: [string, string] | null;
    teachers: string[];
    actions: string[];
  }>({
    dateRange: null,
    teachers: [],
    actions: []
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const assignmentChannel = supabase
      .channel('assignment_history')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'assignment_history',
        },
        () => {
          debug('Assignment history changed, refreshing...');
          loadAssignmentHistory();
        }
      )
      .subscribe();

    const staffChannel = supabase
      .channel('assignment_history_staff')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'staff',
        },
        () => {
          debug('Staff data changed, refreshing...');
          loadStaff();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(assignmentChannel);
      supabase.removeChannel(staffChannel);
    };
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadAssignmentHistory(),
        loadStaff()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      addToast('Failed to load assignment history', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssignmentHistory = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('assignment_history')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(1000); // Limit to last 1000 changes

      if (error) throw error;

      // pit.assignment_history is normalised: typed columns
      // (grid_id/class_id/subject/action/performed_by/timestamp) plus old_values /
      // new_values jsonb that carry the denormalised display fields written by the
      // assignment path (see StabilizedContexts.logAssignmentChange).
      const transformedChanges: AssignmentChange[] = (data || []).map(item => {
        const nv = (item.new_values || {}) as Record<string, any>;
        const ov = (item.old_values || {}) as Record<string, any>;
        const lineId = nv.line_id ?? ov.line_id ?? '';
        return {
          id: item.id,
          timestamp: new Date(item.timestamp),
          user_email: nv.user_email || '',
          // DB actions are assigned|unassigned|modified; the UI has two states —
          // unassigned -> "unassign", everything else (assigned/modified) -> "assign"
          // (both place a teacher). The precise action stays in the DB for audit.
          action: item.action === 'unassigned' ? 'unassign' : 'assign',
          class_id: item.class_id,
          class_name: nv.class_name || item.subject || item.class_id || 'Unknown',
          line_id: lineId,
          line_name: lineId ? `Line ${lineId}` : 'Unknown Line',
          teacher_code: nv.teacher_code ?? null,
          teacher_name: nv.teacher_name ?? ov.teacher_name ?? null,
        };
      });

      setAssignmentChanges(transformedChanges);
    } catch (error) {
      console.error('Error loading assignment history:', error);
      addToast('Failed to load assignment history', 'error');
      setAssignmentChanges([]);
    }
  };

  const loadStaff = async () => {
    if (!user) return;

    try {
      const staffData = await staffService.getAllStaff();
      const transformedStaff: Teacher[] = staffData.map(staff => ({
        id: staff.id,
        name: staff.name,
        staff_id: staff.staff_id,
        department: staff.department || 'Unknown'
      }));
      setTeachers(transformedStaff);
    } catch (error) {
      console.error('Error loading staff:', error);
      addToast('Failed to load staff data', 'error');
      setTeachers([]);
    }
  };

  // Filter changes based on search and filters
  const getFilteredChanges = () => {
    return assignmentChanges.filter(change => {
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          change.class_name?.toLowerCase().includes(query) ||
          change.teacher_name?.toLowerCase().includes(query) ||
          change.teacher_code?.toLowerCase().includes(query) ||
          change.user_email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Date range filter
      if (activeFilters.dateRange) {
        const [start, end] = activeFilters.dateRange;
        const changeDate = new Date(change.timestamp);
        const startDate = new Date(start);
        const endDate = new Date(end);
        endDate.setHours(23, 59, 59, 999); // Include the entire end day
        if (changeDate < startDate || changeDate > endDate) {
          return false;
        }
      }

      // Teacher filter
      if (activeFilters.teachers.length > 0) {
        const teacherMatch = change.teacher_code && activeFilters.teachers.includes(change.teacher_code);
        if (!teacherMatch) return false;
      }

      // Action filter
      if (activeFilters.actions.length > 0) {
        if (!activeFilters.actions.includes(change.action)) {
          return false;
        }
      }

      return true;
    });
  };

  // Get sorted and paginated changes
  const getSortedAndPaginatedChanges = () => {
    const filtered = getFilteredChanges();
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let valueA: any = a[sortBy as keyof AssignmentChange];
      let valueB: any = b[sortBy as keyof AssignmentChange];

      // Handle null values
      if (valueA === null && valueB === null) return 0;
      if (valueA === null) return sortOrder === 'asc' ? -1 : 1;
      if (valueB === null) return sortOrder === 'asc' ? 1 : -1;

      // Compare dates
      if (sortBy === 'timestamp') {
        return sortOrder === 'asc' 
          ? new Date(valueA).getTime() - new Date(valueB).getTime() 
          : new Date(valueB).getTime() - new Date(valueA).getTime();
      }

      // Compare strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      }

      // Compare numbers
      return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
    });

    // Paginate
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    return Math.ceil(getFilteredChanges().length / itemsPerPage);
  };

  // Apply date filter
  const applyDateFilter = () => {
    if (!startDate || !endDate) {
      addToast('Please select both start and end dates', 'error');
      return;
    }
    setActiveFilters({
      ...activeFilters,
      dateRange: [startDate, endDate]
    });
  };

  // Clear date filter
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setActiveFilters({
      ...activeFilters,
      dateRange: null
    });
  };

  // Toggle filter
  const toggleFilter = (type: keyof typeof activeFilters, value: any) => {
    if (type === 'dateRange') return; // Handle date range separately
    
    setActiveFilters(prev => {
      const current = [...prev[type]];
      if (current.includes(value)) {
        return {
          ...prev,
          [type]: current.filter(v => v !== value)
        };
      } else {
        return {
          ...prev,
          [type]: [...current, value]
        };
      }
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setActiveFilters({
      dateRange: null,
      teachers: [],
      actions: []
    });
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
  };

  // Handle sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc'); // Default to descending for new sort field
    }
  };

  // Export history
  const exportHistory = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Class', 'Line', 'Teacher Code', 'Teacher Name'];
    const filteredChanges = getFilteredChanges();
    const csvData = filteredChanges.map(change => [
      change.timestamp.toISOString(),
      change.user_email,
      change.action,
      change.class_name,
      change.line_name || '',
      change.teacher_code || '',
      change.teacher_name || ''
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assignment-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    addToast(`Exported ${filteredChanges.length} assignment changes`, 'success');
  };

  // Get action badge
  const getActionBadge = (action: string) => {
    switch (action) {
      case 'assign':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Assign
          </span>
        );
      case 'unassign':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ink-100 text-ink-900">
            <XCircle className="h-3 w-3 mr-1" />
            Unassign
          </span>
        );
      default:
        return null;
    }
  };

  // Render filter panel
  const renderFilterPanel = () => {
    if (!showFilters) return null;

    const uniqueTeacherCodes = [...new Set(teachers.map(t => t.staff_id))];
    const actions = ['assign', 'unassign'];

    return (
      <div className="mb-6 p-4 bg-ink-50 rounded-lg border border-ink-200">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-ink-700">Filters</h4>
          <button 
            className="text-sm text-navy-600 hover:text-navy-800" 
            onClick={resetFilters}
          >
            Reset All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Date Range */}
          <div>
            <h5 className="text-xs font-medium text-ink-500 mb-2">Date Range</h5>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-ink-500 mb-1">Start Date</label>
                <input 
                  type="date" 
                  className="block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)} 
                />
              </div>
              <div>
                <label className="block text-xs text-ink-500 mb-1">End Date</label>
                <input 
                  type="date" 
                  className="block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)} 
                />
              </div>
              <div className="flex space-x-2">
                <Button variant="secondary" size="sm" onClick={applyDateFilter}>
                  Apply
                </Button>
                <Button variant="secondary" size="sm" onClick={clearDateFilter}>
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {/* Teachers */}
          <div>
            <h5 className="text-xs font-medium text-ink-500 mb-2">Teachers</h5>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {uniqueTeacherCodes.map(teacherCode => {
                const teacher = teachers.find(t => t.staff_id === teacherCode);
                return (
                  <button
                    key={teacherCode}
                    className={`px-2 py-1 rounded-md text-xs font-medium ${
                      activeFilters.teachers.includes(teacherCode)
                        ? 'bg-navy-100 text-navy-800'
                        : 'bg-ink-100 text-ink-900 hover:bg-ink-200'
                    }`}
                    onClick={() => toggleFilter('teachers', teacherCode)}
                  >
                    {teacher?.name || teacherCode} ({teacherCode})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div>
            <h5 className="text-xs font-medium text-ink-500 mb-2">Actions</h5>
            <div className="flex flex-wrap gap-2">
              {actions.map(action => (
                <button
                  key={action}
                  className={`px-2 py-1 rounded-md text-xs font-medium ${
                    activeFilters.actions.includes(action)
                      ? 'bg-navy-100 text-navy-800'
                      : 'bg-ink-100 text-ink-900 hover:bg-ink-200'
                  }`}
                  onClick={() => toggleFilter('actions', action)}
                >
                  {action.charAt(0).toUpperCase() + action.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render change details modal
  const renderChangeDetails = () => {
    if (!selectedChange || !showDetails) return null;
    
    const change = assignmentChanges.find(c => c.id === selectedChange);
    if (!change) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <h3 className="text-section text-ink-900">
                Assignment Change Details
              </h3>
              <button 
                className="text-ink-400 hover:text-ink-500" 
                onClick={() => setShowDetails(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-sm font-medium text-ink-700 mb-2">Change Information</h4>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-ink-500">Timestamp</div>
                    <div className="font-mono tabular-nums text-sm text-ink-900">
                      {change.timestamp.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">User</div>
                    <div className="text-sm text-ink-900">{change.user_email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">Action</div>
                    <div className="text-sm">
                      {getActionBadge(change.action)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-ink-700 mb-2">Class Information</h4>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-ink-500">Class</div>
                    <div className="font-mono tabular-nums text-sm text-ink-900">{change.class_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">Line</div>
                    <div className="font-mono tabular-nums text-sm text-ink-900">{change.line_name}</div>
                  </div>
                  {change.teacher_code && (
                    <div>
                      <div className="text-xs text-ink-500">Teacher</div>
                      <div className="text-sm text-ink-900">
                        {change.teacher_name} ({change.teacher_code})
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-medium text-ink-700 mb-2">Assignment Change</h4>
              <div className="p-4 bg-ink-50 rounded-md border">
                {change.action === 'assign' && (
                  <div className="flex items-center">
                    <div className="flex-1 text-center">
                      <div className="text-xs text-ink-500">Unassigned</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-navy-500 mx-4" />
                    <div className="flex-1 text-center">
                      <div className="text-sm font-medium text-ink-900">
                        {change.teacher_name}
                      </div>
                      <div className="text-xs text-ink-500">Assigned</div>
                    </div>
                  </div>
                )}
                
                {change.action === 'unassign' && (
                  <div className="flex items-center">
                    <div className="flex-1 text-center">
                      <div className="text-sm font-medium text-ink-900">
                        {change.teacher_name}
                      </div>
                      <div className="text-xs text-ink-500">Previously Assigned</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-danger-fg mx-4" />
                    <div className="flex-1 text-center">
                      <div className="text-xs text-ink-500">Unassigned</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setShowDetails(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <div>
        <PageHeader 
          title="Assignment History" 
          description="View and track changes to class assignments over time" 
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 text-navy-500 mx-auto mb-4 animate-spin" />
            <p className="text-ink-500">Loading assignment history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title="Assignment History" 
        description="View and track changes to class assignments over time"
        actions={
          <div className="flex space-x-2">
            <Button 
              variant="secondary" 
              leftIcon={<RefreshCw className="h-4 w-4" />} 
              onClick={loadData}
            >
              Refresh
            </Button>
            <Button 
              variant="secondary" 
              leftIcon={<Download className="h-4 w-4" />} 
              onClick={exportHistory}
            >
              Export History
            </Button>
          </div>
        } 
      />

      <Card className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-card-title text-ink-900">Assignment Changes</h3>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-6">
          <div className="w-full md:w-64">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-ink-400" />
              </div>
              <input 
                type="text" 
                className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" 
                placeholder="Search changes..." 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)} 
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button 
              variant="secondary" 
              size="sm" 
              leftIcon={<ListFilter className="h-4 w-4" />} 
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-ink-500">Show</span>
              <select 
                className="block pl-3 pr-10 py-1 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" 
                value={itemsPerPage} 
                onChange={e => setItemsPerPage(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span className="text-sm text-ink-500">per page</span>
            </div>
          </div>
        </div>

        {renderFilterPanel()}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200">
            <thead className="bg-ink-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100" 
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center">
                    <span>Timestamp</span>
                    {sortBy === 'timestamp' && (
                      sortOrder === 'asc' 
                        ? <ChevronUp className="h-4 w-4 ml-1" /> 
                        : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100" 
                  onClick={() => handleSort('class_name')}
                >
                  <div className="flex items-center">
                    <span>Class</span>
                    {sortBy === 'class_name' && (
                      sortOrder === 'asc' 
                        ? <ChevronUp className="h-4 w-4 ml-1" /> 
                        : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Line
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Change
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100" 
                  onClick={() => handleSort('action')}
                >
                  <div className="flex items-center">
                    <span>Action</span>
                    {sortBy === 'action' && (
                      sortOrder === 'asc' 
                        ? <ChevronUp className="h-4 w-4 ml-1" /> 
                        : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider cursor-pointer hover:bg-ink-100" 
                  onClick={() => handleSort('user_email')}
                >
                  <div className="flex items-center">
                    <span>User</span>
                    {sortBy === 'user_email' && (
                      sortOrder === 'asc' 
                        ? <ChevronUp className="h-4 w-4 ml-1" /> 
                        : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {getSortedAndPaginatedChanges().length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-center text-sm text-ink-500">
                    No assignment changes found matching the filters
                  </td>
                </tr>
              ) : (
                getSortedAndPaginatedChanges().map(change => (
                  <tr key={change.id} className="hover:bg-ink-50">
                    <td className="font-mono tabular-nums px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {change.timestamp.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono tabular-nums text-sm font-medium text-ink-900">
                        {change.class_name}
                      </div>
                    </td>
                    <td className="font-mono tabular-nums px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {change.line_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {change.action === 'assign' && `Assigned to ${change.teacher_name}`}
                      {change.action === 'unassign' && `Unassigned from ${change.teacher_name}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(change.action)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {change.user_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        leftIcon={<Eye className="h-4 w-4" />} 
                        onClick={() => {
                          setSelectedChange(change.id);
                          setShowDetails(true);
                        }}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="flex items-center justify-between border-t border-ink-200 px-4 py-3 sm:px-6 mt-4">
            <div className="flex flex-1 justify-between sm:hidden">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setCurrentPage(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setCurrentPage(currentPage + 1)} 
                disabled={currentPage === getTotalPages()}
              >
                Next
              </Button>
            </div>
            
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-ink-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, getFilteredChanges().length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">
                    {getFilteredChanges().length}
                  </span>{' '}
                  results
                </p>
              </div>
              
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button 
                    onClick={() => setCurrentPage(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-300 hover:bg-ink-50 focus:z-20 focus:outline-offset-0 ${
                      currentPage === 1 ? 'cursor-not-allowed' : 'hover:text-ink-700'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronUp className="h-5 w-5 rotate-270" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page, and pages around current page
                      return page === 1 || page === getTotalPages() || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => {
                      // Add ellipsis where needed
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <Fragment key={page}>
                          {showEllipsis && (
                            <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-ink-700 ring-1 ring-inset ring-ink-300">
                              ...
                            </span>
                          )}
                          <button 
                            onClick={() => setCurrentPage(page)} 
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                              page === currentPage 
                                ? 'bg-navy-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600' 
                                : 'text-ink-900 ring-1 ring-inset ring-ink-300 hover:bg-ink-50 focus:z-20 focus:outline-offset-0'
                            }`}
                          >
                            {page}
                          </button>
                        </Fragment>
                      );
                    })}
                  
                  <button 
                    onClick={() => setCurrentPage(currentPage + 1)} 
                    disabled={currentPage === getTotalPages()} 
                    className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-300 hover:bg-ink-50 focus:z-20 focus:outline-offset-0 ${
                      currentPage === getTotalPages() ? 'cursor-not-allowed' : 'hover:text-ink-700'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <ChevronDown className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-card-title text-ink-900">Assignment History Statistics</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-ink-50 rounded-md p-4 text-center">
            <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">
              {assignmentChanges.length}
            </div>
            <div className="text-sm text-ink-500">Total Changes</div>
          </div>
          
          <div className="bg-ink-50 rounded-md p-4 text-center">
            <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">
              {new Set(assignmentChanges.map(c => c.teacher_code).filter(Boolean)).size}
            </div>
            <div className="text-sm text-ink-500">Teachers Affected</div>
          </div>
          
          <div className="bg-ink-50 rounded-md p-4 text-center">
            <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">
              {new Set(assignmentChanges.map(c => c.class_id)).size}
            </div>
            <div className="text-sm text-ink-500">Classes Modified</div>
          </div>
        </div>
        
        <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
          <div className="flex">
            <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-navy-800 mb-1">
                Assignment History Tracking
              </h4>
              <p className="text-sm text-navy-700">
                All assignment changes are automatically tracked and stored. This provides a complete audit trail 
                of who made changes, when they were made, and what was changed.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Details Modal */}
      {renderChangeDetails()}
    </div>
  );
};

export default AssignmentHistory;